import { Banknote, CreditCard, HandCoins, Wallet } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Field } from '@/components/common/FormBits'
import { cn } from '@/lib/utils'
import { fmtMoney } from '../../../shared/format'
import { PAYMENT_METHODS, VAT_RATES, vatDeductible, vatOf, type CardOption, type DocumentType, type PaymentMethod } from '../../expenses'

const METHOD_ICON: Record<PaymentMethod, typeof Wallet> = { cash: Banknote, personal_card: Wallet, company_card: CreditCard, advance: HandCoins }

/**
 * Ödeme şekli seçimi: nakit, şahsi kart, şirket kartı (yalnız zimmetli kartlar) ve iş avansı (açık bakiye gösterilir; aşan
 * kısım personele geri ödenir). advanceBalance verilmezse (eski kayıt aktarımı) bakiye yazılmaz.
 */
export function PaymentMethodPicker({
  value,
  cardId,
  cards,
  advanceBalance,
  amount,
  onChange,
}: {
  value: PaymentMethod
  cardId: string
  cards: CardOption[]
  advanceBalance?: string
  amount?: number
  onChange: (p: { paymentMethod?: PaymentMethod; cardId?: string }) => void
}) {
  const { t } = useTranslation()
  const balance = Number(advanceBalance ?? 0)
  const hint = (m: PaymentMethod) => {
    if (m === 'company_card' && !cards.length) return t('crm.expenses.noCards')
    if (m === 'advance' && advanceBalance !== undefined) return t('crm.expenses.advanceBalance', { amount: fmtMoney(advanceBalance) })
    return t(`crm.expenses.methodHints.${m}`)
  }
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{t('crm.expenses.paymentMethod')}</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {PAYMENT_METHODS.map((m) => {
          const Icon = METHOD_ICON[m]
          const disabled = m === 'company_card' && !cards.length
          return (
            <button
              key={m}
              type="button"
              disabled={disabled}
              onClick={() => onChange({ paymentMethod: m })}
              className={cn(
                'flex items-start gap-2 rounded-lg border p-3 text-left text-sm transition-colors',
                value === m ? 'border-primary bg-primary/5' : 'hover:bg-muted/40',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <Icon className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
              <span>
                <span className="block font-medium">{t(`crm.expenses.methods.${m}`)}</span>
                <span className="text-xs text-muted-foreground">{hint(m)}</span>
              </span>
            </button>
          )
        })}
      </div>
      {value === 'company_card' ? (
        <Select
          aria-label={t('crm.expenses.card')}
          value={cardId}
          onChange={(id) => onChange({ cardId: id })}
          options={[{ value: '', label: t('crm.expenses.pickCard') }, ...cards.map((c) => ({ value: c.id, label: `${c.name} •••• ${c.last4}` }))]}
        />
      ) : null}
      {value === 'advance' && advanceBalance !== undefined && amount && amount > balance ? (
        <p className="text-xs text-warning">{t('crm.expenses.advanceExceeds', { amount: fmtMoney(Math.max(amount - balance, 0).toFixed(2)) })}</p>
      ) : null}
    </div>
  )
}

/** Belge türü (fiş / fatura); faturada KDV oranı, fatura no ve hesaplanan KDV. */
export function DocumentFields({
  documentType,
  vatRate,
  invoiceNo,
  amount,
  category,
  idPrefix,
  onChange,
}: {
  documentType: DocumentType
  vatRate: string
  invoiceNo: string
  amount: number
  category: string
  idPrefix: string
  onChange: (p: { documentType?: DocumentType; vatRate?: string; invoiceNo?: string }) => void
}) {
  const { t } = useTranslation()
  const vat = vatOf(amount, Number(vatRate))
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Field id={`${idPrefix}-doc`} label={t('crm.expenses.documentType')}>
        <Select
          id={`${idPrefix}-doc`}
          value={documentType}
          onChange={(d) => onChange({ documentType: d as DocumentType, vatRate: d === 'invoice' && vatRate === '0' ? '20' : vatRate })}
          options={(['receipt', 'invoice'] as const).map((d) => ({ value: d, label: t(`crm.expenses.documentTypes.${d}`) }))}
        />
      </Field>
      {documentType === 'invoice' ? (
        <>
          <Field id={`${idPrefix}-vat`} label={t('crm.expenses.vatRate')}>
            <Select id={`${idPrefix}-vat`} value={vatRate} onChange={(r) => onChange({ vatRate: r })} options={VAT_RATES.map((r) => ({ value: r, label: `%${r}` }))} />
          </Field>
          <Field id={`${idPrefix}-inv`} label={t('crm.expenses.invoiceNo')}>
            <Input id={`${idPrefix}-inv`} value={invoiceNo} maxLength={50} onChange={(e) => onChange({ invoiceNo: e.target.value })} />
          </Field>
          <p className="text-xs text-muted-foreground sm:col-span-3">
            {vatDeductible(category) ? t('crm.expenses.vatHint', { vat: fmtMoney(vat.toFixed(2)), net: fmtMoney(Math.max(amount - vat, 0).toFixed(2)) }) : t('crm.expenses.vatNotDeductible')}
          </p>
        </>
      ) : null}
    </div>
  )
}
